import React from 'react';
import { Trash2, Plus, Layers, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Seed } from '@/types/idea-machine';
import { CategoryItem, CategoryWithSeeds, SectionState } from '@/types/admin';

interface CategoryManagementSectionProps {
  categories: CategoryItem[];
  seeds: Seed[];
  newCategory: string;
  setNewCategory: (category: string) => void;
  categorySection: SectionState;
  handleAddCategory: () => void;
  handleRemoveCategory: (categoryId: string) => void;
  handleRemoveSeed: (seedId: string) => void;
}

const CategoryManagementSection: React.FC<CategoryManagementSectionProps> = ({
  categories,
  seeds,
  newCategory,
  setNewCategory,
  categorySection,
  handleAddCategory,
  handleRemoveCategory,
  handleRemoveSeed
}) => {
  // Group seeds by category
  const categoriesWithSeeds: CategoryWithSeeds[] = categories.map(category => ({
    ...category,
    seeds: seeds.filter(seed => seed.category === category.name)
  }));

  return (
    <Card className="border-none shadow-none bg-transparent">
      <CardContent className="px-0">
        {/* Add Category Section */}
        <div className="bg-card border rounded-xl p-6 mb-8 shadow-sm">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <label className="text-sm font-medium mb-2 block">New Category Name</label>
              <Input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className="w-full"
                placeholder="e.g., Productivity, Creative Writing..."
              />
            </div>
            <Button
              onClick={handleAddCategory}
              className="bg-primary hover:bg-primary/90 min-w-[140px]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </div>

          {categorySection.error && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{categorySection.error}</AlertDescription>
            </Alert>
          )}

          {categorySection.success && (
            <Alert className="mt-4 border-green-200 bg-green-50 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
              <AlertDescription>{categorySection.success}</AlertDescription>
            </Alert>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold tracking-tight flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary" />
              Existing Categories
              <Badge variant="secondary" className="ml-2">{categories.length}</Badge>
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categoriesWithSeeds.map((category) => (
              <Card key={category.name} className="flex flex-col h-[320px] overflow-hidden hover:shadow-md transition-shadow duration-200 border-muted">
                <CardHeader className="pb-2 bg-muted/30 border-b">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg font-bold">{category.name}</CardTitle>
                      <CardDescription className="text-xs mt-1">
                        {category.seeds.length} {category.seeds.length === 1 ? 'seed' : 'seeds'} available
                      </CardDescription>
                    </div>
                    {category.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveCategory(category.id!)}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive -mr-2 -mt-2"
                        title="Delete Category"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="flex-1 p-0 overflow-hidden bg-card">
                  <ScrollArea className="h-full">
                    <div className="p-4 space-y-3">
                      {category.seeds.length > 0 ? (
                        category.seeds.map((seed) => (
                          <div
                            key={seed.id}
                            className="group relative bg-muted/20 hover:bg-muted/50 rounded-lg p-3 text-sm border border-transparent hover:border-muted-foreground/20 transition-all"
                          >
                            <div className="pr-6 text-foreground/90 leading-relaxed">
                              {seed.prompt}
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                              onClick={() => handleRemoveSeed(seed.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))
                      ) : (
                        <div className="flex flex-col items-center justify-center h-40 text-muted-foreground text-center p-4">
                          <Sparkles className="h-8 w-8 mb-2 opacity-20" />
                          <p className="text-sm italic">No seeds yet.</p>
                          <p className="text-xs mt-1 opacity-70">Add seeds in the Idea Seeds tab.</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CategoryManagementSection;
